import { AlertColor, Box, Grid, SelectChangeEvent, Skeleton, Typography } from "@mui/material";
import { DashboardCardWorkspace } from "../DashboardCard/DashboardCardWorkspace";
import { EmptyContainer } from "../../../../../components/KeydriveComponents/EmptyContainer/EmptyContainer";
import { useTranslation } from "react-i18next";
import { NoWorkspacesOrganizationIcon } from "../../../../../assets/svg/NoWorkspacesOrganizationIcon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterRangeUsageStorage } from "../DashboardFilter/FilterRangeUsageStorage";
import { KeyDriveSelect } from "../../../../../components/KeydriveComponents/Selects/KeyDriveSelect";
import { Organization } from "../../../../../model/OrganizationModel";
import { GetAdminList, GetListWorkspacesWithStorageSizeByOrgInternalName  } from "../../../../../services/OrganizationServices";
import { useParams } from "react-router-dom";
import { WorkspaceStorageSizeInfoModel } from "../../../../../model/WorkspaceStorageSizeInfoModel";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";
import { KeyDriveSearchBar } from "../../../../../components/KeydriveComponents/SearchBars/KeyDriveSearchBar";

function useVisibility(
  cb: (isVisible: boolean) => void,
  deps: React.DependencyList
): (node: any) => void {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  return useCallback((node) => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }

    intersectionObserver.current = new IntersectionObserver(([entry]) => {
      cb(entry.isIntersecting);
    });

    if (node) intersectionObserver.current.observe(node);
  }, deps);
}

export const DashboardDataWorkspaceList = () => {
    const { t } = useTranslation();

    const { nameOrg } = useParams();
    const [minUsage, setMinUsage] = useState<number | undefined>();
    const [maxUsage, setMaxUsage] = useState<number | undefined>();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectOrganizationSearch, setSelectOrganizationSearch] = useState<string>("");
    const [workspaceData, setWorkspaceData] = useState<WorkspaceStorageSizeInfoModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string | undefined>(undefined);
    const [alertColor, setAlertColor] = useState<AlertColor>("success");
    const [skip, setSkip] = useState<number>(0);
    const [workspaceMax, setWorkspaceMax] = useState<boolean>(true);
    const [workspaceSearchTerm, setWorkspaceSearchTerm] = useState<string>("");
    const LIMIT = 15;

    useEffect(() => {
        if (!nameOrg) return;
        const fetchData = () => {
            setIsLoading(true);
            setSkip(0);
            
            GetAdminList(0, 999, false)
            .then(data => {
                setOrganizations(data);
            })
            .catch((err) =>{
                setMessage(err.response.data);
                setAlertColor("error");
            })
            .finally(() => {
                setIsLoading(false)
            })
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (!nameOrg) return;
        setSkip(0);
        setSelectOrganizationSearch(nameOrg!);
    }, [nameOrg]);

    useEffect(() => {
        setIsLoading(true);
        setSkip(0);
        if (selectOrganizationSearch !== "") {
            fetchWorkspaceWithSearch();
        }
    }, [selectOrganizationSearch]);

    useEffect(() => {
        if (selectOrganizationSearch) {
            setSkip(0);
            fetchWorkspace(selectOrganizationSearch);
        }
    }, [workspaceSearchTerm]);

    function handleOrganization(e: SelectChangeEvent): void {
        setSelectOrganizationSearch(e.target.value);
    }

    const filteredData = useMemo(() => {
        if (!workspaceData) return [];
        return workspaceData.filter((item) => {
            const usage = item.usedSpace ?? 0;

            if (minUsage !== undefined && usage > minUsage) return false;
            if (maxUsage !== undefined && usage < maxUsage) return false;
            return true;
        });
    }, [workspaceData, minUsage, maxUsage]);
    
    const renderContent = () => {
        if (isLoading) {
            return Array.from(Array(6)).map((_, index) => (
                <Grid item xs={2} sm={4} md={4} key={index} >
                    <Box
                        sx={{
                            background: "#FFFFFF 0% 0% no-repeat padding-box",
                            borderRadius: "4px",
                            opacity: 1,
                            border: "1px solid #0000001A",
                            width: "100%",
                            height: "93px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "12px",
                        }}
                    >
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="rectangular" width="100%" height={12} sx={{ borderRadius: "2px" }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Skeleton variant="text" width="30%" height={16} />
                            <Skeleton variant="text" width="30%" height={16} />
                        </Box>
                    </Box>
                </Grid>
            ));
        }

        if (filteredData) {
            if (filteredData.length <= 0 && !isLoading) {
                return (
                    <Box width="100%" marginTop={2.5} height="100%">
                        <EmptyContainer
                            content={t('AdminConsole.NoFoundWorkspaceSubtittle')}
                            title={t('AdminConsole.NotFoundWorkspace')}
                            icon={<NoWorkspacesOrganizationIcon />}
                        />
                    </Box>
                );
            }

            return filteredData.map((item, index) => (
                <Grid item xs={2} sm={4} md={4} key={index} sx={{ width: "100%" }} ref={workspaceData[workspaceData.length - 1]?.id == item?.id && workspaceMax ? lastBook : null}>
                    <DashboardCardWorkspace {...item} />
                </Grid>
            ));
        }

        return null;
    };

    function clearFilteredData(): void {
        setSkip(0);
        fetchWorkspace(selectOrganizationSearch);
    }

    function fetchWorkspace(selectedOrganization?: string): void {
        if (!nameOrg) return;
        setIsLoading(true);
        GetListWorkspacesWithStorageSizeByOrgInternalName(selectedOrganization ? selectedOrganization : nameOrg, skip, LIMIT, workspaceSearchTerm)
        .then(wsData => {
            if (wsData.length < LIMIT) {
                setWorkspaceMax(false);
            }

            setWorkspaceData(wsData);
        })
        .catch((err) =>{
            setMessage(err.response.data);
            setAlertColor("error");
        })
        .finally(() => {
            setIsLoading(false);
        })
    };

    function fetchWorkspaceWithSearch() {
        setIsLoading(true);
        setSkip(0);
        GetListWorkspacesWithStorageSizeByOrgInternalName(selectOrganizationSearch, skip, LIMIT, workspaceSearchTerm)
        .then(data => {

            if (data.length < LIMIT) {
                setWorkspaceMax(false);
            }

            setWorkspaceData(data);
        })
        .catch((err) =>{
            setMessage(err.response.data);
            setAlertColor("error");
        })
        .finally(() => {
            setIsLoading(false);
        })
    }

    const lastBook = useVisibility(
        (visible) => {
            if (visible) {
                GetListWorkspacesWithStorageSizeByOrgInternalName(selectOrganizationSearch!, skip, LIMIT, workspaceSearchTerm)
                .then(data => {
                    if (data.length < LIMIT) {
                        setWorkspaceMax(false);
                    };

                    setSkip(previous => previous + data.length);
                    setWorkspaceData(previous => [...previous, ...data]);
                })
            }
        }, [skip, workspaceData]);

    return (
        <>
            {/* Header */}
            <KeyDriveSearchBar 
                value={workspaceSearchTerm}
                onChange={(e) => { setWorkspaceSearchTerm(e.target.value) }}
            />
            <Box marginTop="12px">
                <KeyDriveSelect<Organization>
                    renderItem={(item) => (
                        <>
                            {item?.name}
                        </>
                    )}
                    keyExtract={(item) => item?.internalName?.toString()}
                    items={organizations}
                    onChange={handleOrganization}
                    value={selectOrganizationSearch}
                    label={t("AdminConsole.Organizations")}
                    needFullWidth
                />
            </Box>
            <Box sx={{ margin: '5px 0 25px 0' }} display={"flex"} justifyContent="space-between" alignItems="top">
                <Typography
                    sx={{
                        font: "normal normal 600 16px/21px Segoe UI",
                        letterSpacing: "0px",
                        color: "#2D2D2D",
                    }}
                >
                    Workspaces
                </Typography>
                <FilterRangeUsageStorage disabled={isLoading} minUsage={minUsage} maxUsage={maxUsage} setMinUsage={setMinUsage} setMaxUsage={setMaxUsage} onItemClear={clearFilteredData} />
            </Box>
            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 8, lg: 12 }} sx={{ overflowY: 'auto', paddingRight: '8px' }}>
                {renderContent()}
            </Grid>
            <KeydriveSnackbar 
                open={message != undefined}
                message={message!}
                onClose={() => setMessage(undefined)}
                variant={alertColor}
            />
        </>
    );
};
